import React, { useEffect, useMemo, useRef, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useLang, useTheme } from "@/contexts/AppContexts";
import { ZoomIn, ZoomOut, Maximize2, Eye, EyeOff } from "lucide-react";

const severityStrokes = {
  low: "hsl(var(--forensic-overlay-low))",
  medium: "hsl(var(--forensic-overlay-med))",
  high: "hsl(var(--forensic-overlay-high))",
};
const severityFills = {
  low: "hsl(var(--forensic-overlay-low) / 0.16)",
  medium: "hsl(var(--forensic-overlay-med) / 0.16)",
  high: "hsl(var(--forensic-overlay-high) / 0.16)",
};

const ImageViewer = ({
  imageSrc,
  elaSrc,
  imageSize = [1024, 1024],
  regions = [],
  selectedRegionId,
  onRegionClick,
}) => {
  const { t } = useLang();
  const { theme } = useTheme();
  const [showOriginal, setShowOriginal] = useState(true);
  const [showBboxes, setShowBboxes] = useState(true);
  const [showEla, setShowEla] = useState(false);
  const [elaOpacity, setElaOpacity] = useState(0.55);
  const [zoom, setZoom] = useState(1);
  const containerRef = useRef(null);

  // Reset zoom when a new image is loaded
  useEffect(() => {
    setZoom(1);
  }, [imageSrc]);

  const [imgW, imgH] = useMemo(() => {
    if (Array.isArray(imageSize) && imageSize.length === 2) {
      return [imageSize[0] || 1024, imageSize[1] || 1024];
    }
    return [1024, 1024];
  }, [imageSize]);

  const handleZoomIn = () => setZoom((z) => Math.min(3, z + 0.25));
  const handleZoomOut = () => setZoom((z) => Math.max(0.5, z - 0.25));
  const handleZoomFit = () => setZoom(1);

  return (
    <Card className="overflow-hidden" data-testid="image-viewer-card">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-2 border-b bg-card/60 px-3 py-2">
        <div className="flex flex-wrap items-center gap-1.5">
          <ToggleChip
            active={showOriginal}
            onClick={() => setShowOriginal((v) => !v)}
            icon={showOriginal ? Eye : EyeOff}
            label={t("viewer.original")}
            testId="overlay-toggle-original"
          />
          <ToggleChip
            active={showBboxes}
            onClick={() => setShowBboxes((v) => !v)}
            label={t("viewer.bboxes")}
            testId="overlay-toggle-bboxes"
            badgeCount={regions.length}
          />
          <ToggleChip
            active={showEla}
            onClick={() => setShowEla((v) => !v)}
            label={t("viewer.ela")}
            testId="overlay-toggle-ela"
          />
          {showEla && elaSrc && (
            <div className="flex items-center gap-2 ml-2 min-w-[180px]">
              <span className="text-xs text-muted-foreground font-mono-plex">
                {t("viewer.opacity")}
              </span>
              <Slider
                value={[Math.round(elaOpacity * 100)]}
                min={0}
                max={100}
                step={5}
                onValueChange={(v) => setElaOpacity((v?.[0] ?? 0) / 100)}
                className="w-24"
                data-testid="overlay-opacity-slider"
              />
              <span className="text-xs font-mono-plex tabular-nums w-9 text-right">
                {Math.round(elaOpacity * 100)}%
              </span>
            </div>
          )}
        </div>
        <div className="flex items-center gap-1">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleZoomOut}
                  data-testid="viewer-zoom-out"
                  aria-label="Zoom out"
                >
                  <ZoomOut className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Zoom out</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleZoomIn}
                  data-testid="viewer-zoom-in"
                  aria-label="Zoom in"
                >
                  <ZoomIn className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Zoom in</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleZoomFit}
                  data-testid="viewer-zoom-fit"
                  aria-label="Fit"
                >
                  <Maximize2 className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Fit</TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <span className="font-mono-plex text-xs tabular-nums w-12 text-right">
            {Math.round(zoom * 100)}%
          </span>
        </div>
      </div>

      {/* Canvas */}
      <div
        className="relative overflow-auto scrollbar-thin bg-[hsl(var(--forensic-canvas))] bg-grid"
        style={{ maxHeight: "min(72vh, 760px)" }}
        ref={containerRef}
        data-testid="image-viewer"
      >
        <div
          className="relative inline-block origin-top-left transition-transform"
          style={{ transform: `scale(${zoom})` }}
        >
          {imageSrc && showOriginal && (
            <img
              src={imageSrc}
              alt="original"
              className="block max-w-full select-none pointer-events-none"
              style={{ display: "block" }}
              draggable={false}
              data-testid="viewer-original-image"
            />
          )}
          {(!imageSrc || !showOriginal) && (
            <div
              style={{ width: imgW, height: imgH, maxWidth: "100%" }}
              className="flex min-h-[320px] items-center justify-center bg-muted text-muted-foreground"
            >
              {!imageSrc && (
                <div className="flex flex-col items-center gap-2 px-6 text-center text-sm">
                  <EyeOff className="h-7 w-7" />
                  <span>{t("viewer.image_unavailable")}</span>
                </div>
              )}
            </div>
          )}
          {showEla && elaSrc && (
            <img
              src={elaSrc}
              alt="ELA heatmap"
              className="absolute inset-0 max-w-full pointer-events-none"
              style={{
                opacity: elaOpacity,
                mixBlendMode: theme === "dark" ? "screen" : "multiply",
                width: "100%",
                height: "100%",
                objectFit: "contain",
                objectPosition: "top left",
              }}
              data-testid="viewer-ela-image"
            />
          )}
          {showBboxes && regions && regions.length > 0 && (
            <svg
              className="absolute inset-0"
              viewBox={`0 0 1000 1000`}
              preserveAspectRatio="none"
              style={{ width: "100%", height: "100%" }}
              data-testid="viewer-bbox-overlay"
            >
              {regions.map((r, i) => {
                const [y1, x1, y2, x2] = r.bbox || [0, 0, 0, 0];
                const w = x2 - x1;
                const h = y2 - y1;
                const isSelected = selectedRegionId === i;
                return (
                  <g
                    key={i}
                    style={{ cursor: "pointer" }}
                    onClick={() => onRegionClick && onRegionClick(i)}
                    data-testid="bbox-overlay-item"
                  >
                    <rect
                      x={x1}
                      y={y1}
                      width={w}
                      height={h}
                      fill={severityFills[r.severity] || severityFills.medium}
                      stroke={severityStrokes[r.severity] || severityStrokes.medium}
                      strokeWidth={isSelected ? 5 : 3}
                      strokeDasharray={isSelected ? "0" : "6,4"}
                      rx="6"
                      vectorEffect="non-scaling-stroke"
                    />
                    <foreignObject
                      x={x1}
                      y={Math.max(0, y1 - 26)}
                      width={Math.min(300, Math.max(80, w))}
                      height={26}
                    >
                      <div
                        xmlns="http://www.w3.org/1999/xhtml"
                        className="inline-flex items-center gap-1 rounded-md border bg-background/90 px-1.5 py-0.5 text-[10px] font-medium font-mono-plex shadow-sm backdrop-blur"
                        style={{
                          color: severityStrokes[r.severity] || severityStrokes.medium,
                          borderColor: severityStrokes[r.severity] || severityStrokes.medium,
                        }}
                      >
                        <span
                          className="inline-block h-1.5 w-1.5 rounded-full"
                          style={{ background: severityStrokes[r.severity] }}
                        />
                        {r.label || "Region"}
                      </div>
                    </foreignObject>
                  </g>
                );
              })}
            </svg>
          )}
        </div>

        {/* Severity legend */}
        {showBboxes && regions && regions.length > 0 && (
          <div className="absolute bottom-3 right-3 flex items-center gap-2 rounded-lg border bg-background/85 px-2.5 py-1.5 text-[11px] backdrop-blur shadow-sm">
            <span className="font-mono-plex text-muted-foreground">
              {t("viewer.legend_title")}
            </span>
            <LegendDot color={severityStrokes.low} label={t("viewer.severity_low")} />
            <LegendDot color={severityStrokes.medium} label={t("viewer.severity_medium")} />
            <LegendDot color={severityStrokes.high} label={t("viewer.severity_high")} />
          </div>
        )}
      </div>
    </Card>
  );
};

const ToggleChip = ({ active, onClick, label, icon: Icon, testId, badgeCount }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    aria-pressed={active}
    className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
      active
        ? "border-primary/40 bg-primary/10 text-foreground"
        : "border-border bg-muted/40 text-muted-foreground hover:bg-muted"
    }`}
  >
    {Icon && <Icon className="h-3 w-3" />}
    <span>{label}</span>
    {badgeCount != null && badgeCount > 0 && (
      <span className="ml-0.5 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-primary/20 px-1 text-[10px] font-bold tabular-nums text-primary">
        {badgeCount}
      </span>
    )}
  </button>
);

const LegendDot = ({ color, label }) => (
  <span className="inline-flex items-center gap-1">
    <span
      className="inline-block h-2 w-2 rounded-full"
      style={{ background: color }}
    />
    {label}
  </span>
);

export { ImageViewer };
export default ImageViewer;
